/**
 * Generated by orval 🍺
 * Do not edit manually.
 * TrainLoop Platform V2 API
 * Model evaluation workbench API
 * OpenAPI spec version: 0.0.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  EvalGroupAnalysisResponse,
  EvalGroupDetailResponse,
  EvalGroupSummary,
  EvalRenameRequest,
  EvalRenameResponse,
  EvalRunDetailResponse,
  EvalTaskDetailResponse,
  EvalsOverviewResponse,
  GetGroupsForModelApiEvalGroupsForModelModelIdGetParams,
  GroupSyncResponse,
  GroupSyncUpdate,
  HTTPValidationError,
  RolloutTrajectory,
} from "../model";

import { orvalFetch } from "../../client.ts";
import type { ErrorType } from "../../client.ts";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponse200 = {
  data: RolloutTrajectory;
  status: 200;
};

export type getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponseSuccess =
  getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponse200 & {
    headers: Headers;
  };
export type getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponseError =
  getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponse422 & {
    headers: Headers;
  };

export type getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponse =
  | getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponseSuccess
  | getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponseError;

export const getGetCaseRolloutApiEvalGroupsRolloutsTraceIdGetUrl = (
  traceId: string,
) => {
  return `/api/eval-groups/rollouts/${traceId}`;
};

/**
 * The full trajectory behind one eval-run case, from the data bucket
 * (eval-rollouts/{team_slug}/{trace_id}.json). 404 when no rollout was
 * exported for the trace — pushes are not required to carry one.
 * Opening one is recorded in the team's audit log (TRA-1626).
 * @summary Get Case Rollout
 */
export const getCaseRolloutApiEvalGroupsRolloutsTraceIdGet = async (
  traceId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponse> => {
  return orvalFetch<getCaseRolloutApiEvalGroupsRolloutsTraceIdGetResponse>(
    getGetCaseRolloutApiEvalGroupsRolloutsTraceIdGetUrl(traceId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetCaseRolloutApiEvalGroupsRolloutsTraceIdGetQueryKey = (
  traceId: string,
) => {
  return [`/api/eval-groups/rollouts/${traceId}`] as const;
};

export const getGetCaseRolloutApiEvalGroupsRolloutsTraceIdGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  traceId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetCaseRolloutApiEvalGroupsRolloutsTraceIdGetQueryKey(traceId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>>
  > = ({ signal }) =>
    getCaseRolloutApiEvalGroupsRolloutsTraceIdGet(traceId, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: traceId !== null && traceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCaseRolloutApiEvalGroupsRolloutsTraceIdGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>>
  >;
export type GetCaseRolloutApiEvalGroupsRolloutsTraceIdGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetCaseRolloutApiEvalGroupsRolloutsTraceIdGet<
  TData = Awaited<
    ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  traceId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetCaseRolloutApiEvalGroupsRolloutsTraceIdGet<
  TData = Awaited<
    ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  traceId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetCaseRolloutApiEvalGroupsRolloutsTraceIdGet<
  TData = Awaited<
    ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  traceId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Case Rollout
 */

export function useGetCaseRolloutApiEvalGroupsRolloutsTraceIdGet<
  TData = Awaited<
    ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  traceId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getCaseRolloutApiEvalGroupsRolloutsTraceIdGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetCaseRolloutApiEvalGroupsRolloutsTraceIdGetQueryOptions(
      traceId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getOverviewApiEvalGroupsOverviewGetResponse200 = {
  data: EvalsOverviewResponse;
  status: 200;
};

export type getOverviewApiEvalGroupsOverviewGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getOverviewApiEvalGroupsOverviewGetResponseSuccess =
  getOverviewApiEvalGroupsOverviewGetResponse200 & {
    headers: Headers;
  };
export type getOverviewApiEvalGroupsOverviewGetResponseError =
  getOverviewApiEvalGroupsOverviewGetResponse422 & {
    headers: Headers;
  };

export type getOverviewApiEvalGroupsOverviewGetResponse =
  | getOverviewApiEvalGroupsOverviewGetResponseSuccess
  | getOverviewApiEvalGroupsOverviewGetResponseError;

export const getGetOverviewApiEvalGroupsOverviewGetUrl = () => {
  return `/api/eval-groups/overview`;
};

/**
 * Everything the top-level /evals screen needs: group cards + timeline.
 * @summary Get Overview
 */
export const getOverviewApiEvalGroupsOverviewGet = async (
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getOverviewApiEvalGroupsOverviewGetResponse> => {
  return orvalFetch<getOverviewApiEvalGroupsOverviewGetResponse>(
    getGetOverviewApiEvalGroupsOverviewGetUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetOverviewApiEvalGroupsOverviewGetQueryKey = () => {
  return [`/api/eval-groups/overview`] as const;
};

export const getGetOverviewApiEvalGroupsOverviewGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
  TError = ErrorType<HTTPValidationError>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof orvalFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOverviewApiEvalGroupsOverviewGetQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>
  > = ({ signal }) =>
    getOverviewApiEvalGroupsOverviewGet({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetOverviewApiEvalGroupsOverviewGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>
>;
export type GetOverviewApiEvalGroupsOverviewGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetOverviewApiEvalGroupsOverviewGet<
  TData = Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
          TError,
          Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOverviewApiEvalGroupsOverviewGet<
  TData = Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
          TError,
          Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOverviewApiEvalGroupsOverviewGet<
  TData = Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Overview
 */

export function useGetOverviewApiEvalGroupsOverviewGet<
  TData = Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOverviewApiEvalGroupsOverviewGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetOverviewApiEvalGroupsOverviewGetQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getGroupApiEvalGroupsSlugGetResponse200 = {
  data: EvalGroupDetailResponse;
  status: 200;
};

export type getGroupApiEvalGroupsSlugGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getGroupApiEvalGroupsSlugGetResponseSuccess =
  getGroupApiEvalGroupsSlugGetResponse200 & {
    headers: Headers;
  };
export type getGroupApiEvalGroupsSlugGetResponseError =
  getGroupApiEvalGroupsSlugGetResponse422 & {
    headers: Headers;
  };

export type getGroupApiEvalGroupsSlugGetResponse =
  | getGroupApiEvalGroupsSlugGetResponseSuccess
  | getGroupApiEvalGroupsSlugGetResponseError;

export const getGetGroupApiEvalGroupsSlugGetUrl = (slug: string) => {
  return `/api/eval-groups/${slug}`;
};

/**
 * The group screen: step-chart history + task rows.
 * @summary Get Group
 */
export const getGroupApiEvalGroupsSlugGet = async (
  slug: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getGroupApiEvalGroupsSlugGetResponse> => {
  return orvalFetch<getGroupApiEvalGroupsSlugGetResponse>(
    getGetGroupApiEvalGroupsSlugGetUrl(slug),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetGroupApiEvalGroupsSlugGetQueryKey = (slug: string) => {
  return [`/api/eval-groups/${slug}`] as const;
};

export const getGetGroupApiEvalGroupsSlugGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetGroupApiEvalGroupsSlugGetQueryKey(slug);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>
  > = ({ signal }) =>
    getGroupApiEvalGroupsSlugGet(slug, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: slug !== null && slug !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGroupApiEvalGroupsSlugGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>
>;
export type GetGroupApiEvalGroupsSlugGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetGroupApiEvalGroupsSlugGet<
  TData = Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
          TError,
          Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetGroupApiEvalGroupsSlugGet<
  TData = Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
          TError,
          Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetGroupApiEvalGroupsSlugGet<
  TData = Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Group
 */

export function useGetGroupApiEvalGroupsSlugGet<
  TData = Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGroupApiEvalGroupsSlugGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetGroupApiEvalGroupsSlugGetQueryOptions(
    slug,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type renameGroupApiEvalGroupsSlugPatchResponse200 = {
  data: EvalRenameResponse;
  status: 200;
};

export type renameGroupApiEvalGroupsSlugPatchResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type renameGroupApiEvalGroupsSlugPatchResponseSuccess =
  renameGroupApiEvalGroupsSlugPatchResponse200 & {
    headers: Headers;
  };
export type renameGroupApiEvalGroupsSlugPatchResponseError =
  renameGroupApiEvalGroupsSlugPatchResponse422 & {
    headers: Headers;
  };

export type renameGroupApiEvalGroupsSlugPatchResponse =
  | renameGroupApiEvalGroupsSlugPatchResponseSuccess
  | renameGroupApiEvalGroupsSlugPatchResponseError;

export const getRenameGroupApiEvalGroupsSlugPatchUrl = (slug: string) => {
  return `/api/eval-groups/${slug}`;
};

/**
 * Retitle an eval group (TRA-947) — label only, the slug is the key.
 *
 * Locks the name: groups are re-pushed by pipelines on every run, and an
 * unlocked rename would be silently reverted by the next upsert
 * (eval_ingest.upsert_group honors the lock).
 * @summary Rename Group
 */
export const renameGroupApiEvalGroupsSlugPatch = async (
  slug: string,
  evalRenameRequest: EvalRenameRequest,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<renameGroupApiEvalGroupsSlugPatchResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<renameGroupApiEvalGroupsSlugPatchResponse>(
    getRenameGroupApiEvalGroupsSlugPatchUrl(slug),
    {
      ...options,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(evalRenameRequest),
    },
  );
};

export const getRenameGroupApiEvalGroupsSlugPatchMutationKey = () =>
  ["renameGroupApiEvalGroupsSlugPatch"] as const;

export const getRenameGroupApiEvalGroupsSlugPatchMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof renameGroupApiEvalGroupsSlugPatch>>,
    TError,
    RenameGroupApiEvalGroupsSlugPatchMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof renameGroupApiEvalGroupsSlugPatch>>,
  TError,
  RenameGroupApiEvalGroupsSlugPatchMutationVariables,
  TContext
> => {
  const mutationKey = getRenameGroupApiEvalGroupsSlugPatchMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof renameGroupApiEvalGroupsSlugPatch>>,
    RenameGroupApiEvalGroupsSlugPatchMutationVariables
  > = (props) => {
    const { slug, data } = props ?? {};

    return renameGroupApiEvalGroupsSlugPatch(slug, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RenameGroupApiEvalGroupsSlugPatchMutationResult = NonNullable<
  Awaited<ReturnType<typeof renameGroupApiEvalGroupsSlugPatch>>
>;
export type RenameGroupApiEvalGroupsSlugPatchMutationBody = EvalRenameRequest;
export type RenameGroupApiEvalGroupsSlugPatchMutationError =
  ErrorType<HTTPValidationError>;
export type RenameGroupApiEvalGroupsSlugPatchMutationVariables = {
  slug: string;
  data: EvalRenameRequest;
};

/**
 * @summary Rename Group
 */
export const useRenameGroupApiEvalGroupsSlugPatch = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof renameGroupApiEvalGroupsSlugPatch>>,
      TError,
      RenameGroupApiEvalGroupsSlugPatchMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof renameGroupApiEvalGroupsSlugPatch>>,
  TError,
  RenameGroupApiEvalGroupsSlugPatchMutationVariables,
  TContext
> => {
  return useMutation(
    getRenameGroupApiEvalGroupsSlugPatchMutationOptions(options),
    queryClient,
  );
};
export type renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponse200 = {
  data: EvalRenameResponse;
  status: 200;
};

export type renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponseSuccess =
  renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponse200 & {
    headers: Headers;
  };
export type renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponseError =
  renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponse422 & {
    headers: Headers;
  };

export type renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponse =
  | renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponseSuccess
  | renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponseError;

export const getRenameTaskApiEvalGroupsSlugTasksTaskSlugPatchUrl = (
  slug: string,
  taskSlug: string,
) => {
  return `/api/eval-groups/${slug}/tasks/${taskSlug}`;
};

/**
 * Retitle one task in a group (TRA-947) — same lock semantics as the
 * group rename above (eval_ingest.upsert_task honors it).
 * @summary Rename Task
 */
export const renameTaskApiEvalGroupsSlugTasksTaskSlugPatch = async (
  slug: string,
  taskSlug: string,
  evalRenameRequest: EvalRenameRequest,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<renameTaskApiEvalGroupsSlugTasksTaskSlugPatchResponse>(
    getRenameTaskApiEvalGroupsSlugTasksTaskSlugPatchUrl(slug, taskSlug),
    {
      ...options,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(evalRenameRequest),
    },
  );
};

export const getRenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationKey = () =>
  ["renameTaskApiEvalGroupsSlugTasksTaskSlugPatch"] as const;

export const getRenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof renameTaskApiEvalGroupsSlugTasksTaskSlugPatch>>,
    TError,
    RenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof renameTaskApiEvalGroupsSlugTasksTaskSlugPatch>>,
  TError,
  RenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationVariables,
  TContext
> => {
  const mutationKey =
    getRenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof renameTaskApiEvalGroupsSlugTasksTaskSlugPatch>>,
    RenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationVariables
  > = (props) => {
    const { slug, taskSlug, data } = props ?? {};

    return renameTaskApiEvalGroupsSlugTasksTaskSlugPatch(
      slug,
      taskSlug,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type RenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof renameTaskApiEvalGroupsSlugTasksTaskSlugPatch>>
  >;
export type RenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationBody =
  EvalRenameRequest;
export type RenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationError =
  ErrorType<HTTPValidationError>;
export type RenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationVariables = {
  slug: string;
  taskSlug: string;
  data: EvalRenameRequest;
};

/**
 * @summary Rename Task
 */
export const useRenameTaskApiEvalGroupsSlugTasksTaskSlugPatch = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof renameTaskApiEvalGroupsSlugTasksTaskSlugPatch>>,
      TError,
      RenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof renameTaskApiEvalGroupsSlugTasksTaskSlugPatch>>,
  TError,
  RenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationVariables,
  TContext
> => {
  return useMutation(
    getRenameTaskApiEvalGroupsSlugTasksTaskSlugPatchMutationOptions(options),
    queryClient,
  );
};
export type getTaskApiEvalGroupsSlugTasksTaskSlugGetResponse200 = {
  data: EvalTaskDetailResponse;
  status: 200;
};

export type getTaskApiEvalGroupsSlugTasksTaskSlugGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getTaskApiEvalGroupsSlugTasksTaskSlugGetResponseSuccess =
  getTaskApiEvalGroupsSlugTasksTaskSlugGetResponse200 & {
    headers: Headers;
  };
export type getTaskApiEvalGroupsSlugTasksTaskSlugGetResponseError =
  getTaskApiEvalGroupsSlugTasksTaskSlugGetResponse422 & {
    headers: Headers;
  };

export type getTaskApiEvalGroupsSlugTasksTaskSlugGetResponse =
  | getTaskApiEvalGroupsSlugTasksTaskSlugGetResponseSuccess
  | getTaskApiEvalGroupsSlugTasksTaskSlugGetResponseError;

export const getGetTaskApiEvalGroupsSlugTasksTaskSlugGetUrl = (
  slug: string,
  taskSlug: string,
) => {
  return `/api/eval-groups/${slug}/tasks/${taskSlug}`;
};

/**
 * The task screen: stat row, description, and the runs table.
 * @summary Get Task
 */
export const getTaskApiEvalGroupsSlugTasksTaskSlugGet = async (
  slug: string,
  taskSlug: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getTaskApiEvalGroupsSlugTasksTaskSlugGetResponse> => {
  return orvalFetch<getTaskApiEvalGroupsSlugTasksTaskSlugGetResponse>(
    getGetTaskApiEvalGroupsSlugTasksTaskSlugGetUrl(slug, taskSlug),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetTaskApiEvalGroupsSlugTasksTaskSlugGetQueryKey = (
  slug: string,
  taskSlug: string,
) => {
  return [`/api/eval-groups/${slug}/tasks/${taskSlug}`] as const;
};

export const getGetTaskApiEvalGroupsSlugTasksTaskSlugGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  taskSlug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTaskApiEvalGroupsSlugTasksTaskSlugGetQueryKey(slug, taskSlug);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>
  > = ({ signal }) =>
    getTaskApiEvalGroupsSlugTasksTaskSlugGet(slug, taskSlug, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled:
      slug !== null &&
      slug !== undefined &&
      taskSlug !== null &&
      taskSlug !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTaskApiEvalGroupsSlugTasksTaskSlugGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>
>;
export type GetTaskApiEvalGroupsSlugTasksTaskSlugGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetTaskApiEvalGroupsSlugTasksTaskSlugGet<
  TData = Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  taskSlug: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
          TError,
          Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTaskApiEvalGroupsSlugTasksTaskSlugGet<
  TData = Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  taskSlug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
          TError,
          Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTaskApiEvalGroupsSlugTasksTaskSlugGet<
  TData = Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  taskSlug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Task
 */

export function useGetTaskApiEvalGroupsSlugTasksTaskSlugGet<
  TData = Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  taskSlug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTaskApiEvalGroupsSlugTasksTaskSlugGet>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetTaskApiEvalGroupsSlugTasksTaskSlugGetQueryOptions(
    slug,
    taskSlug,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type setGroupSyncApiEvalGroupsSlugSyncPatchResponse200 = {
  data: GroupSyncResponse;
  status: 200;
};

export type setGroupSyncApiEvalGroupsSlugSyncPatchResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type setGroupSyncApiEvalGroupsSlugSyncPatchResponseSuccess =
  setGroupSyncApiEvalGroupsSlugSyncPatchResponse200 & {
    headers: Headers;
  };
export type setGroupSyncApiEvalGroupsSlugSyncPatchResponseError =
  setGroupSyncApiEvalGroupsSlugSyncPatchResponse422 & {
    headers: Headers;
  };

export type setGroupSyncApiEvalGroupsSlugSyncPatchResponse =
  | setGroupSyncApiEvalGroupsSlugSyncPatchResponseSuccess
  | setGroupSyncApiEvalGroupsSlugSyncPatchResponseError;

export const getSetGroupSyncApiEvalGroupsSlugSyncPatchUrl = (slug: string) => {
  return `/api/eval-groups/${slug}/sync`;
};

/**
 * Turn the nightly benchmark inventory on or off for one group. This is
 * a UI-owned toggle, like the renames above.
 * @summary Set Group Sync
 */
export const setGroupSyncApiEvalGroupsSlugSyncPatch = async (
  slug: string,
  groupSyncUpdate: GroupSyncUpdate,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<setGroupSyncApiEvalGroupsSlugSyncPatchResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return orvalFetch<setGroupSyncApiEvalGroupsSlugSyncPatchResponse>(
    getSetGroupSyncApiEvalGroupsSlugSyncPatchUrl(slug),
    {
      ...options,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getHeaders(options?.headers),
      },
      body: JSON.stringify(groupSyncUpdate),
    },
  );
};

export const getSetGroupSyncApiEvalGroupsSlugSyncPatchMutationKey = () =>
  ["setGroupSyncApiEvalGroupsSlugSyncPatch"] as const;

export const getSetGroupSyncApiEvalGroupsSlugSyncPatchMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setGroupSyncApiEvalGroupsSlugSyncPatch>>,
    TError,
    SetGroupSyncApiEvalGroupsSlugSyncPatchMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof orvalFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setGroupSyncApiEvalGroupsSlugSyncPatch>>,
  TError,
  SetGroupSyncApiEvalGroupsSlugSyncPatchMutationVariables,
  TContext
> => {
  const mutationKey = getSetGroupSyncApiEvalGroupsSlugSyncPatchMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setGroupSyncApiEvalGroupsSlugSyncPatch>>,
    SetGroupSyncApiEvalGroupsSlugSyncPatchMutationVariables
  > = (props) => {
    const { slug, data } = props ?? {};

    return setGroupSyncApiEvalGroupsSlugSyncPatch(slug, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetGroupSyncApiEvalGroupsSlugSyncPatchMutationResult = NonNullable<
  Awaited<ReturnType<typeof setGroupSyncApiEvalGroupsSlugSyncPatch>>
>;
export type SetGroupSyncApiEvalGroupsSlugSyncPatchMutationBody =
  GroupSyncUpdate;
export type SetGroupSyncApiEvalGroupsSlugSyncPatchMutationError =
  ErrorType<HTTPValidationError>;
export type SetGroupSyncApiEvalGroupsSlugSyncPatchMutationVariables = {
  slug: string;
  data: GroupSyncUpdate;
};

/**
 * @summary Set Group Sync
 */
export const useSetGroupSyncApiEvalGroupsSlugSyncPatch = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setGroupSyncApiEvalGroupsSlugSyncPatch>>,
      TError,
      SetGroupSyncApiEvalGroupsSlugSyncPatchMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof setGroupSyncApiEvalGroupsSlugSyncPatch>>,
  TError,
  SetGroupSyncApiEvalGroupsSlugSyncPatchMutationVariables,
  TContext
> => {
  return useMutation(
    getSetGroupSyncApiEvalGroupsSlugSyncPatchMutationOptions(options),
    queryClient,
  );
};
export type getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponse200 = {
  data: EvalGroupAnalysisResponse;
  status: 200;
};

export type getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponseSuccess =
  getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponse200 & {
    headers: Headers;
  };
export type getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponseError =
  getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponse422 & {
    headers: Headers;
  };

export type getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponse =
  | getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponseSuccess
  | getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponseError;

export const getGetGroupAnalysisApiEvalGroupsSlugAnalysisGetUrl = (
  slug: string,
) => {
  return `/api/eval-groups/${slug}/analysis`;
};

/**
 * The group analysis page: header stats + per-task ledger.
 *
 * Tasks come back easiest → hardest (headline score desc, unscored
 * last) so the score-distribution chart plots them by index.
 * @summary Get Group Analysis
 */
export const getGroupAnalysisApiEvalGroupsSlugAnalysisGet = async (
  slug: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponse> => {
  return orvalFetch<getGroupAnalysisApiEvalGroupsSlugAnalysisGetResponse>(
    getGetGroupAnalysisApiEvalGroupsSlugAnalysisGetUrl(slug),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetGroupAnalysisApiEvalGroupsSlugAnalysisGetQueryKey = (
  slug: string,
) => {
  return [`/api/eval-groups/${slug}/analysis`] as const;
};

export const getGetGroupAnalysisApiEvalGroupsSlugAnalysisGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetGroupAnalysisApiEvalGroupsSlugAnalysisGetQueryKey(slug);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>>
  > = ({ signal }) =>
    getGroupAnalysisApiEvalGroupsSlugAnalysisGet(slug, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: slug !== null && slug !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGroupAnalysisApiEvalGroupsSlugAnalysisGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>>
  >;
export type GetGroupAnalysisApiEvalGroupsSlugAnalysisGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetGroupAnalysisApiEvalGroupsSlugAnalysisGet<
  TData = Awaited<
    ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetGroupAnalysisApiEvalGroupsSlugAnalysisGet<
  TData = Awaited<
    ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetGroupAnalysisApiEvalGroupsSlugAnalysisGet<
  TData = Awaited<
    ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Group Analysis
 */

export function useGetGroupAnalysisApiEvalGroupsSlugAnalysisGet<
  TData = Awaited<
    ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getGroupAnalysisApiEvalGroupsSlugAnalysisGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetGroupAnalysisApiEvalGroupsSlugAnalysisGetQueryOptions(slug, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponse200 = {
  data: EvalRunDetailResponse;
  status: 200;
};

export type getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponseSuccess =
  getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponse200 & {
    headers: Headers;
  };
export type getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponseError =
  getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponse422 & {
    headers: Headers;
  };

export type getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponse =
  | getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponseSuccess
  | getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponseError;

export const getGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetUrl = (
  slug: string,
  taskSlug: string,
  runId: string,
) => {
  return `/api/eval-groups/${slug}/tasks/${taskSlug}/runs/${runId}`;
};

/**
 * The run screen: provenance, metrics, per-case results. The cases
 * carry the inputs and the model's answers, so opening the run is
 * recorded in the team's audit log (TRA-1626).
 * @summary Get Run
 */
export const getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet = async (
  slug: string,
  taskSlug: string,
  runId: string,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponse> => {
  return orvalFetch<getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetResponse>(
    getGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetUrl(
      slug,
      taskSlug,
      runId,
    ),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetQueryKey = (
  slug: string,
  taskSlug: string,
  runId: string,
) => {
  return [`/api/eval-groups/${slug}/tasks/${taskSlug}/runs/${runId}`] as const;
};

export const getGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  taskSlug: string,
  runId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetQueryKey(
      slug,
      taskSlug,
      runId,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>>
  > = ({ signal }) =>
    getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet(slug, taskSlug, runId, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled:
      slug !== null &&
      slug !== undefined &&
      taskSlug !== null &&
      taskSlug !== undefined &&
      runId !== null &&
      runId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>>
  >;
export type GetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet<
  TData = Awaited<
    ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  taskSlug: string,
  runId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet<
  TData = Awaited<
    ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  taskSlug: string,
  runId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet<
  TData = Awaited<
    ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  taskSlug: string,
  runId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Run
 */

export function useGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet<
  TData = Awaited<
    ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  slug: string,
  taskSlug: string,
  runId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetRunApiEvalGroupsSlugTasksTaskSlugRunsRunIdGetQueryOptions(
      slug,
      taskSlug,
      runId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getGroupsForModelApiEvalGroupsForModelModelIdGetResponse200 = {
  data: EvalGroupSummary[];
  status: 200;
};

export type getGroupsForModelApiEvalGroupsForModelModelIdGetResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getGroupsForModelApiEvalGroupsForModelModelIdGetResponseSuccess =
  getGroupsForModelApiEvalGroupsForModelModelIdGetResponse200 & {
    headers: Headers;
  };
export type getGroupsForModelApiEvalGroupsForModelModelIdGetResponseError =
  getGroupsForModelApiEvalGroupsForModelModelIdGetResponse422 & {
    headers: Headers;
  };

export type getGroupsForModelApiEvalGroupsForModelModelIdGetResponse =
  | getGroupsForModelApiEvalGroupsForModelModelIdGetResponseSuccess
  | getGroupsForModelApiEvalGroupsForModelModelIdGetResponseError;

export const getGetGroupsForModelApiEvalGroupsForModelModelIdGetUrl = (
  modelId: string,
  params?: GetGroupsForModelApiEvalGroupsForModelModelIdGetParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/eval-groups/for-model/${modelId}?${stringifiedParams}`
    : `/api/eval-groups/for-model/${modelId}`;
};

/**
 * This model's best eval-group scores, highest first.
 *
 * Eval runs carry a free-text `model_slug` with no FK, so the association
 * is declared on the model (`eval_model_slug`, defaulting to its own
 * slug): a team may serve `wilson-v1` while their pipeline scores it as
 * `cartage-1`, and renaming one shouldn't silently empty this list.
 *
 * Scores use the same rollup as the group cards — the run set is filtered
 * to this model, nothing else changes, so a number here means the same
 * thing as the number on /evals. Groups this model was never scored in
 * are omitted rather than shown as a blank bar.
 * @summary Get Groups For Model
 */
export const getGroupsForModelApiEvalGroupsForModelModelIdGet = async (
  modelId: string,
  params?: GetGroupsForModelApiEvalGroupsForModelModelIdGetParams,
  options?: Parameters<typeof orvalFetch>[1],
): Promise<getGroupsForModelApiEvalGroupsForModelModelIdGetResponse> => {
  return orvalFetch<getGroupsForModelApiEvalGroupsForModelModelIdGetResponse>(
    getGetGroupsForModelApiEvalGroupsForModelModelIdGetUrl(modelId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetGroupsForModelApiEvalGroupsForModelModelIdGetQueryKey = (
  modelId: string,
  params?: GetGroupsForModelApiEvalGroupsForModelModelIdGetParams,
) => {
  return [
    `/api/eval-groups/for-model/${modelId}`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetGroupsForModelApiEvalGroupsForModelModelIdGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetGroupsForModelApiEvalGroupsForModelModelIdGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetGroupsForModelApiEvalGroupsForModelModelIdGetQueryKey(
      modelId,
      params,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>>
  > = ({ signal }) =>
    getGroupsForModelApiEvalGroupsForModelModelIdGet(modelId, params, {
      signal,
      ...requestOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: modelId !== null && modelId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGroupsForModelApiEvalGroupsForModelModelIdGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>>
  >;
export type GetGroupsForModelApiEvalGroupsForModelModelIdGetQueryError =
  ErrorType<HTTPValidationError>;

export function useGetGroupsForModelApiEvalGroupsForModelModelIdGet<
  TData = Awaited<
    ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params: undefined | GetGroupsForModelApiEvalGroupsForModelModelIdGetParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetGroupsForModelApiEvalGroupsForModelModelIdGet<
  TData = Awaited<
    ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetGroupsForModelApiEvalGroupsForModelModelIdGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
          >,
          TError,
          Awaited<
            ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
          >
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetGroupsForModelApiEvalGroupsForModelModelIdGet<
  TData = Awaited<
    ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetGroupsForModelApiEvalGroupsForModelModelIdGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get Groups For Model
 */

export function useGetGroupsForModelApiEvalGroupsForModelModelIdGet<
  TData = Awaited<
    ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  modelId: string,
  params?: GetGroupsForModelApiEvalGroupsForModelModelIdGetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof getGroupsForModelApiEvalGroupsForModelModelIdGet>
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof orvalFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetGroupsForModelApiEvalGroupsForModelModelIdGetQueryOptions(
      modelId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}
